import { useEffect, useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { useAppDispatch } from '@/utils/typeHelpers'
import { selectIsInUnservicedFreeTrial } from '@/selectors/user.selectors'

import { Alert, Icon, Link, Text, HeroCard, Badge, Switch } from '@/ui'
import { ArticleCard } from '../shared/ArticleCard'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import {
  useReselector,
  useToggle,
  useTrackFinancialInsightsButtonClick,
} from '@/utils/sharedHooks'
import {
  ARTICLE_TYPE,
  FinancialInsightsButtonType,
  INSIGHTS_TYPE,
} from '../shared/utils'
import { InsightsBreakdownTable } from '../shared/InsightsBreakdownTable'
import { selectActivePlaidFinancialAccounts } from '@/selectors/financeSelectors'
import { UncategorizedTransactionBanner } from '../shared/UncategorizedTransactionBanner'
import { isEmpty } from 'lodash'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { CurrentPanel } from '../shared/CurrentPanel'
import { TrackInsightsSection } from '../shared/TrackInsightsSection'
import { DisplaySampleDataBanner } from './DisplaySampleDataBanner'
import { fetchInsightsTableDropdownOptions } from '../shared/financeActions'
import {
  useInsightsDatePicker,
  InsightsDatePicker,
} from '../shared/InsightsDatePicker'
import { InsightBreadcrumb } from '../shared/InsightBreadcrumb'
import { ImageBackgroundColors, TextColors, TypographyStyles } from '@/styles'

export const InsightsExpensesPanel = () => {
  const dispatch = useAppDispatch()
  const [displayBanner, toggleBanner] = useToggle(true)
  const [shouldDisplaySampleData, setShouldDisplaySampleData] = useState(false)
  const [displaySampleDataBanner, setDisplaySampleDataBanner] = useState(false)
  const [years, setYears] = useState<number[]>([])
  const [searchParams] = useSearchParams()

  const { monthParam, yearParam } = useMemo(() => {
    return {
      monthParam: searchParams.get('month'),
      yearParam: searchParams.get('year'),
    }
  }, [searchParams])

  const { yearFilter, monthFilter, inputDate, setYearFilter, setMonthFilter } =
    useInsightsDatePicker(monthParam, yearParam)

  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const linkedPlaidAccounts = useReselector(selectActivePlaidFinancialAccounts)
  const hasLinkedPlaidAccounts = !isEmpty(linkedPlaidAccounts)
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)

  const trackInsights = useTrackFinancialInsightsButtonClick()

  useEffect(() => {
    dispatch(fetchFinancialAccountsIfNeeded())
  }, [dispatch])

  useEffect(() => {
    const fetchYears = async () => {
      const years = await fetchInsightsTableDropdownOptions(
        INSIGHTS_TYPE.EXPENSES
      )(dispatch)

      if (years?.length) {
        setYears(years)
        setDisplaySampleDataBanner(false)
      } else {
        setYears([2024, 2023, 2022])
        setDisplaySampleDataBanner(true)
      }
    }
    fetchYears()
  }, [dispatch])

  const closeBanner = () => {
    toggleBanner()
    trackInsights({
      button: FinancialInsightsButtonType.CLOSE_BANNER,
      insightsType: INSIGHTS_TYPE.EXPENSES,
    })
  }

  const closeSampleDataBanner = () => {
    setDisplaySampleDataBanner(false)
  }

  return (
    <div style={{ marginTop: shouldDisplaySampleData ? -24 : 0 }}>
      {shouldDisplaySampleData && (
        <div className="sticky top-0 z-10 bg-orange-500 px-5 py-4 -mx-4 sm:-mx-6 md:-mx-14">
          <Text
            variant={TypographyStyles.BODY_SM}
            color={TextColors.WHITE}
            className="text-center"
          >
            <Icon
              icon={faCircleExclamation}
              color={TextColors.WHITE}
              className="mr-2"
            />
            This is a <b>demo</b> page using <b>sample data.</b>
          </Text>
        </div>
      )}

      <div className="space-y-6">
        {!shouldDisplaySampleData && (
          <InsightBreadcrumb month={monthFilter} year={yearFilter} />
        )}

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Text variant={TypographyStyles.H1}>Expenses</Text>
          <InsightsDatePicker
            monthFilter={monthFilter}
            yearFilter={yearFilter}
            handleMonthFilterChange={setMonthFilter}
            handleYearFilterChange={setYearFilter}
            insightsType={INSIGHTS_TYPE.INCOME}
          />
        </div>

        {displaySampleDataBanner && !isInUnservicedFreeTrial && (
          <DisplaySampleDataBanner
            headerText="Your expense insights are coming soon"
            imageUrl="https://heard-images.s3.amazonaws.com/assets/notepad-pencil.png"
            onClose={closeSampleDataBanner}
            bodyContent={
              <>
                Once we&apos;ve completed your books, you&apos;ll be able to see
                personalized insights based on your transaction data.
                <br />
                <br />
                In the meantime, you can view this page with sample data.
                <br />
                <br />
                <Switch
                  checked={shouldDisplaySampleData}
                  onChange={() =>
                    setShouldDisplaySampleData(!shouldDisplaySampleData)
                  }
                  label="View with sample data"
                />
              </>
            }
          />
        )}

        {displayBanner && !displaySampleDataBanner && (
          <HeroCard
            title="What are Business Expenses?"
            imageSrc="https://heard-images.s3.amazonaws.com/assets/chart-tablet-banner.svg"
            onClose={closeBanner}
            imageAlt="What are Business Expenses?"
            imageColor={ImageBackgroundColors.ORANGE}
          >
            <Text variant={TypographyStyles.BODY_SM}>
              Business expenses are ordinary and necessary costs incurred to
              operate your business, like rent, professional fees, and software
              services.
            </Text>
            <Text variant={TypographyStyles.BODY_SM}>
              For less frequent expenses like licenses or education, decide on a
              percentage to set aside in your{' '}
              <Link href="/allocation-guides" newPage={false}>
                Allocation guide
              </Link>
              .
            </Text>
          </HeroCard>
        )}

        {!hasLinkedPlaidAccounts && (
          <Alert variant="warning">
            <Text variant={TypographyStyles.BODY_SM}>
              You do not have a connected bank account.{' '}
              <Link href="/accounts#connected-institutions" newPage={false}>
                Link Business Account
              </Link>
            </Text>
          </Alert>
        )}

        <UncategorizedTransactionBanner inputDate={inputDate} />

        <CurrentPanel
          inputDate={inputDate}
          insightsType={INSIGHTS_TYPE.EXPENSES}
          shouldDisplaySampleData={shouldDisplaySampleData}
        />

        <br />

        <TrackInsightsSection
          inputDate={inputDate}
          type={INSIGHTS_TYPE.EXPENSES}
          shouldDisplaySampleData={shouldDisplaySampleData}
        />

        <br />
        <Text variant={TypographyStyles.H2} className="my-4">
          Expenses Breakdown{' '}
          {shouldDisplaySampleData && (
            <Badge color="orange" className="rounded-md ml-2">
              Sample data
            </Badge>
          )}
        </Text>

        <InsightsBreakdownTable
          inputDate={inputDate}
          insightsType={INSIGHTS_TYPE.EXPENSES}
          shouldDisplaySampleData={shouldDisplaySampleData}
          years={years}
        />

        <Text variant={TypographyStyles.H2} className="my-4">
          Learn More
        </Text>

        <div
          className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}
        >
          <ArticleCard
            title="How to Build a Budget for Your Therapy Practice"
            type={ARTICLE_TYPE.ARTICLE}
            url="https://www.joinheard.com/articles/how-to-build-a-budget-for-your-therapy-practice"
            insightsType={INSIGHTS_TYPE.EXPENSES}
          />
          <ArticleCard
            title="How to Determine if Something is a Tax Deduction for Therapists (with Examples)"
            type={ARTICLE_TYPE.ARTICLE}
            url="https://www.joinheard.com/articles/how-to-determine-if-something-is-a-tax-deduction-for-therapists-with-examples"
            insightsType={INSIGHTS_TYPE.EXPENSES}
          />
          <ArticleCard
            title="What Therapists Need to Know About Deducting Education Expenses"
            type={ARTICLE_TYPE.ARTICLE}
            url="https://www.joinheard.com/articles/what-therapists-need-to-know-about-deducting-education-expenses"
            insightsType={INSIGHTS_TYPE.EXPENSES}
          />
        </div>
      </div>
    </div>
  )
}
